import {
  ButtonBase,
  Checkbox,
  Divider,
  InputAdornment,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { debounce } from '@mui/material/utils';
import { MdSearch } from '@teamworksdev/icons';
import { useMemo, useState } from 'react';
import { RefCallBack } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export type SelectableOption =
  | string
  | {
      id: string;
    };

export const SelectFilter = <T extends SelectableOption>({
  selectedOptions,
  options,
  ref,
  placeholder,
  ariaLabel,
  onChange,
  optionToName,
  fullWidth,
  disabled,
  disableSearch = false,
  showSelectAll = true,
  multiselect = true,
}: {
  selectedOptions: T[];
  options: T[];
  ref?: RefCallBack;
  placeholder?: string;
  ariaLabel?: string;
  fullWidth?: boolean;
  disabled?: boolean;
  onChange: (selected: T[]) => void;
  optionToName: (option: T) => string | React.ReactNode;
  disableSearch?: boolean;
  showSelectAll?: boolean;
  multiselect?: boolean;
}) => {
  const { t } = useTranslation();
  const {
    palette: { grey, greyscaleLight },
  } = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSetSearchTerm = useMemo(
    () => debounce((value) => setSearchTerm(value), 300),
    [],
  );
  const filteredOptions = useMemo(() => {
    if (disableSearch) {
      return options;
    }
    return options.filter((option) =>
      (optionToName?.(option) || option.toString())
        .toString()
        .toLowerCase()
        .includes(searchTerm.toLowerCase()),
    );
  }, [options, searchTerm, optionToName, disableSearch]);
  const getId = (option: T) =>
    typeof option === 'string' ? option : option.id;
  return (
    <Select
      displayEmpty
      ref={ref}
      multiple={multiselect}
      fullWidth={fullWidth}
      disabled={disabled}
      inputProps={{
        'aria-label':
          ariaLabel?.trim() || placeholder?.trim() || t('selectOption'),
      }}
      value={
        multiselect
          ? selectedOptions.map((opt) => getId(opt))
          : selectedOptions[0]
            ? getId(selectedOptions[0])
            : ''
      }
      // disablePortal to avoid infinite loop from components fighting for focus
      MenuProps={{
        autoFocus: false,
        disablePortal: true,
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'center',
        },
        style: {
          maxHeight: 600,
        },
      }}
      sx={{
        '& .MuiList-root': {
          padding: 0,
        },
        '.MuiOutlinedInput-notchedOutline': { border: 'none' },
        fontWeight: 'bold',
        fontSize: '14px',
        backgroundColor: 'transparent',
      }}
      className="hover:bg-gray-200"
      onClose={() => {
        setSearchTerm('');
        debouncedSetSearchTerm.clear();
      }}
      onChange={(e) => {
        // single-select: e.target.value is a scalar; multiselect: an array.
        // Scalar must compare exactly — .includes would substring-match
        // (e.g. "12".includes("1")).
        const selected = options.filter((opt) =>
          multiselect
            ? e.target.value.includes(getId(opt))
            : e.target.value === getId(opt),
        );
        onChange(selected);
      }}
      renderValue={(selected) => {
        const curSelectedOptions = options.filter((opt) =>
          multiselect ? selected.includes(getId(opt)) : selected === getId(opt),
        );
        return (
          <Stack gap={1} direction="row" flexWrap="nowrap" alignItems="center">
            <div>{placeholder}</div>
            {curSelectedOptions.length <= options.length &&
              curSelectedOptions.length > 0 && (
                <div
                  className="inline-flex rounded-full text-white w-6 h-6 justify-center items-center font-bold"
                  style={{ background: greyscaleLight[600] }}
                >
                  {curSelectedOptions.length}
                </div>
              )}
          </Stack>
        );
      }}
    >
      <ListSubheader
        sx={{
          margin: 0,
          padding: 0,
          py: disableSearch ? 0 : 1,
          '& .MuiTextField-root': { marginTop: 0 },
        }}
      >
        {!disableSearch && (
          <div className="flex flex-col">
            <TextField
              autoFocus
              variant="outlined"
              placeholder={t('search')}
              inputProps={{ 'aria-label': t('search') }}
              InputProps={{
                startAdornment: (
                  <InputAdornment
                    position="start"
                    sx={{ paddingLeft: 0, marginLeft: 0 }}
                  >
                    <MdSearch size={24} className="mt-[3px]" />
                  </InputAdornment>
                ),
              }}
              sx={{
                marginX: 2,
                '& .MuiInputBase-root': { paddingLeft: 1 },
                border: 1,
                borderColor: grey[300],
              }}
              onChange={(e) => {
                debouncedSetSearchTerm(e.currentTarget.value);
              }}
              onKeyDown={(e) => {
                if (e.key !== 'Escape') {
                  // Prevents autoselecting item while typing (default Select behaviour)
                  e.stopPropagation();
                }
              }}
            />
            {showSelectAll && (
              <>
                <ButtonBase
                  component="div"
                  className="flex items-center"
                  sx={{
                    justifyContent: 'flex-start',
                    backgroundColor: 'transparent',
                    p: 0,
                    pr: 0.5,
                    marginTop: 2,
                    marginX: 1,
                    ':hover': {
                      backgroundColor: grey[100],
                    },
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    if (selectedOptions.length === options.length) {
                      onChange([]);
                    } else {
                      onChange(options);
                    }
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={selectedOptions.length === options.length}
                    sx={{ pr: 0.5 }}
                    inputProps={{ 'aria-label': t('selectAll') }}
                    onChange={() => {
                      if (selectedOptions.length === options.length) {
                        onChange([]);
                      } else {
                        onChange(options);
                      }
                    }}
                  />
                  <Typography variant="body2">{t('all')}</Typography>
                </ButtonBase>
                <Divider variant="middle" />
              </>
            )}
          </div>
        )}
      </ListSubheader>
      {filteredOptions.map((opt) => {
        return (
          <MenuItem
            key={getId(opt)}
            value={getId(opt)}
            autoFocus={false}
            sx={{
              backgroundColor: 'transparent',
              p: 0,
              pr: 0.5,
              marginX: 1,
            }}
          >
            <Checkbox
              size="small"
              checked={!!selectedOptions.find((s) => getId(s) === getId(opt))}
              sx={{ pr: 0.5 }}
            />
            <Typography variant="body2">{optionToName(opt)}</Typography>
          </MenuItem>
        );
      })}
    </Select>
  );
};
